import { useState } from 'react';
import { ListFilter, Pencil, QrCode, RefreshCw, Trash2, UserPlus, Users } from 'lucide-react';
import {
  formatAttendanceBreakdown,
  invitePreviewUrl,
  type CampaignRecipientGroup,
  type InviteRecipient,
  type RsvpResponseValue,
} from '@peles/shared';
import {
  EntityList,
  FilterSelect,
  ShareLinkButton,
  StatusBadge,
  type EntityListColumn,
} from '../../common';
import { DropdownMenu } from '../../common/DropdownMenu';
import { useDialog } from '../../../context/DialogContext';
import { useCan } from '../../../hooks/useCan';
import {
  isGoingOut,
  useAddCampaignRecipients,
  useCampaignRecipientPage,
  useCampaignRecipients,
  useRemoveCampaignRecipient,
  useResendCampaignRecipient,
} from '../../../hooks/useInvites';
import { addCampaignRecipients } from '../../../api/invites';
import { AddRecipientsModal } from '../AddRecipientsModal';
import { StepFooter } from './StepFooter';
import { RecipientEditModal } from '../RecipientEditModal';
import { AddByFilterModal, RESPONSE_CHOICES } from './AddByFilterModal';
import { RecipientQrModal } from './RecipientQrModal';
import { RECIPIENT_STATUS, RESPONSE_BADGE } from './recipientLabels';
import type { CampaignStepProps } from './CampaignDetailsStep';

const PAGE_SIZE = 25;

const GROUPS: { id: CampaignRecipientGroup | 'all'; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'not_sent', label: 'Not sent' },
  { id: 'sent', label: 'Sent' },
  { id: 'opened', label: 'Opened' },
  { id: 'answered', label: 'Answered' },
  { id: 'bounced', label: 'Bounced' },
  { id: 'failed', label: 'Failed' },
];

/**
 * Step 4: who the invitation goes to — a page at a time, by where each
 * invitation stands and what each guest answered, with each guest's link,
 * QR code, answer and a resend at hand.
 */
export function CampaignGuestsStep({ campaign, eventId, onNext }: CampaignStepProps) {
  const { alert, confirm } = useDialog();
  const canSend = useCan('messages.guests');
  const [group, setGroup] = useState<CampaignRecipientGroup | 'all'>('all');
  const [responses, setResponses] = useState<RsvpResponseValue[]>([]);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [picking, setPicking] = useState(false);
  const [byFilter, setByFilter] = useState(false);
  const [qrFor, setQrFor] = useState<InviteRecipient | null>(null);
  const [editing, setEditing] = useState<InviteRecipient | null>(null);

  const { data, isLoading } = useCampaignRecipientPage(
    campaign.id,
    {
      page,
      pageSize: PAGE_SIZE,
      groups: group === 'all' ? undefined : [group],
      responses,
      q,
    },
    { poll: isGoingOut(campaign) },
  );
  // Only while picking: who is already on it, so they are not offered again.
  const { data: everyone = [] } = useCampaignRecipients(picking ? campaign.id : undefined);
  const add = useAddCampaignRecipients();
  const remove = useRemoveCampaignRecipient();
  const resend = useResendCampaignRecipient();

  const shareText = `${campaign.subject || campaign.name || 'You are invited!'} —`;

  const addEveryone = async () => {
    try {
      const preview = await addCampaignRecipients(campaign.id, { allGuests: true, dryRun: true });
      if (preview.added === 0) {
        await alert({ message: 'Every guest is already on this campaign.', variant: 'info' });
        return;
      }
      const ok = await confirm({
        message: `Add the ${preview.added} guest${preview.added === 1 ? '' : 's'} not on this campaign yet?`,
        confirmLabel: 'Add',
      });
      if (!ok) return;
      const result = await add.mutateAsync({ campaignId: campaign.id, input: { allGuests: true } });
      await alert({
        message: `Added ${result.added} guest${result.added === 1 ? '' : 's'}.`,
        variant: 'success',
      });
    } catch (err) {
      await alert({
        message: err instanceof Error ? err.message : 'Could not add the guests',
        variant: 'error',
      });
    }
  };

  const handleResend = async (recipient: InviteRecipient) => {
    const ok = await confirm({
      message: `Send ${recipient.guestName ?? 'this guest'} the invitation again?`,
      confirmLabel: 'Send again',
    });
    if (!ok) return;
    try {
      const result = await resend.mutateAsync({
        campaignId: campaign.id,
        recipientId: recipient.id,
      });
      await alert(
        result.sent
          ? { message: 'Sent again.', variant: 'success' }
          : result.skipped
            ? { message: 'This guest has no email address to send to.', variant: 'warning' }
            : { message: 'The invitation could not be sent. Try again later.', variant: 'error' },
      );
    } catch (err) {
      await alert({
        message: err instanceof Error ? err.message : 'Could not send it again',
        variant: 'error',
      });
    }
  };

  const handleRemove = async (recipient: InviteRecipient) => {
    const ok = await confirm({
      message: `Remove ${recipient.guestName ?? 'this guest'} from the campaign? Their invitation link stops working.`,
      variant: 'danger',
      confirmLabel: 'Remove',
    });
    if (!ok) return;
    try {
      await remove.mutateAsync({ campaignId: campaign.id, recipientId: recipient.id });
    } catch (err) {
      await alert({
        message: err instanceof Error ? err.message : 'Could not remove the guest',
        variant: 'error',
      });
    }
  };

  const iconButton = 'p-1.5 rounded text-gray-400 hover:text-primary-600';
  const columns: EntityListColumn<InviteRecipient>[] = [
    {
      key: 'guest',
      header: 'Guest',
      render: (r) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-gray-900">{r.guestName ?? '—'}</div>
          <div className="truncate text-xs text-gray-500">{r.guestEmail || 'No email'}</div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Invitation',
      render: (r) => (
        <StatusBadge
          label={RECIPIENT_STATUS[r.status].label}
          color={RECIPIENT_STATUS[r.status].color}
        />
      ),
    },
    {
      key: 'response',
      header: 'Answer',
      render: (r) => {
        const badge = RESPONSE_BADGE[r.response ?? 'PENDING'];
        return (
          <div>
            <StatusBadge label={badge.label} color={badge.color} />
            {r.attendingCount ? (
              <div className="mt-0.5 text-xs text-gray-500">
                {formatAttendanceBreakdown(r.adultCount ?? 0, r.kidCount ?? 0)}
              </div>
            ) : null}
          </div>
        );
      },
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          {canSend && r.channel === 'EMAIL' && (
            <button
              type="button"
              title="Send again"
              aria-label={`Send ${r.guestName ?? 'guest'} the invitation again`}
              onClick={() => void handleResend(r)}
              className={iconButton}
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          )}
          <ShareLinkButton
            compact
            url={invitePreviewUrl(window.location.origin, r.token)}
            text={shareText}
            title={r.guestName ?? 'Invitation'}
            label={r.guestName ?? 'guest'}
            phone={r.guestPhone}
          />
          <button
            type="button"
            title="QR code"
            aria-label={`Show QR code for ${r.guestName ?? 'guest'}`}
            onClick={() => setQrFor(r)}
            className={iconButton}
          >
            <QrCode className="h-4 w-4" />
          </button>
          <button
            type="button"
            title="Enter their answer"
            aria-label={`Edit RSVP for ${r.guestName ?? 'guest'}`}
            onClick={() => setEditing(r)}
            className={iconButton}
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            type="button"
            title="Remove from the campaign"
            aria-label={`Remove ${r.guestName ?? 'guest'}`}
            onClick={() => void handleRemove(r)}
            className="p-1.5 rounded text-gray-400 hover:text-red-600"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  const counts = data?.counts;
  // Not yet loaded is not "nobody": the hint waits for the count.
  const nobodyYet = counts?.all === 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Invitation status">
          {GROUPS.map((g) => (
            <button
              key={g.id}
              type="button"
              aria-pressed={group === g.id}
              onClick={() => {
                setGroup(g.id);
                setPage(1);
              }}
              className={`rounded-full border px-3 py-1 text-sm ${
                group === g.id
                  ? 'border-primary-600 bg-primary-50 text-primary-800'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {g.label}
              {counts && <span className="ml-1 text-xs text-gray-500">{counts[g.id]}</span>}
            </button>
          ))}
        </div>
        <DropdownMenu
          align="end"
          ariaLabel="Add guests"
          trigger={(props) => (
            <button
              type="button"
              {...props}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm text-white hover:bg-primary-700"
            >
              <UserPlus className="h-4 w-4" aria-hidden />
              Add guests
            </button>
          )}
          items={[
            {
              id: 'pick',
              label: 'Pick guests…',
              icon: <UserPlus className="h-4 w-4" />,
              onSelect: () => setPicking(true),
            },
            {
              id: 'filter',
              label: 'Add by filter…',
              icon: <ListFilter className="h-4 w-4" />,
              onSelect: () => setByFilter(true),
            },
            {
              id: 'all',
              label: 'Everyone not on it yet',
              icon: <Users className="h-4 w-4" />,
              onSelect: () => void addEveryone(),
            },
          ]}
        />
      </div>

      <EntityList
        items={data?.items ?? []}
        columns={columns}
        getKey={(r) => r.id}
        isLoading={isLoading}
        page={page}
        pageSize={PAGE_SIZE}
        total={data?.total}
        onPageChange={setPage}
        searchValue={q}
        onSearchChange={(value) => {
          setQ(value);
          setPage(1);
        }}
        searchPlaceholder="Search by name or email"
        filterBar={
          <FilterSelect
            label="Answer"
            multiple
            items={RESPONSE_CHOICES}
            value={responses}
            onChange={(v) => {
              setResponses(v as RsvpResponseValue[]);
              setPage(1);
            }}
            getValue={(o) => o.id}
            getLabel={(o) => o.label}
          />
        }
        emptyMessage={
          counts?.all === 0
            ? 'Nobody is on this campaign yet. Add guests to invite them, or continue and share a link instead.'
            : 'No guests match.'
        }
      />

      {nobodyYet && (
        <p className="text-sm text-gray-600">
          Inviting with a link instead? Continue without adding anybody: on Review &amp; send,
          create a shared link, and everyone who answers it is added to the guest list.
        </p>
      )}
      <StepFooter onNext={onNext} nextLabel={nobodyYet ? 'Continue without guests' : 'Continue'} />

      <AddRecipientsModal
        open={picking}
        onClose={() => setPicking(false)}
        eventId={eventId}
        campaignId={campaign.id}
        existingGuestIds={everyone.map((r) => r.guestId)}
      />
      <AddByFilterModal
        open={byFilter}
        onClose={() => setByFilter(false)}
        eventId={eventId}
        campaignId={campaign.id}
      />
      <RecipientQrModal recipient={qrFor} onClose={() => setQrFor(null)} />
      <RecipientEditModal
        open={!!editing}
        onClose={() => setEditing(null)}
        campaignId={campaign.id}
        recipient={editing}
      />
    </div>
  );
}
